import { RumConfigurationDto } from '../api/CoreWebApi';

export type RumPortal = 'public' | 'admin';

/** What the RUM service needs; mapped from the site metadata DTO in {@link readRumConfig}. */
export interface TellerOnlineRumConfig {
    enabled: boolean;
    appMonitorId: string;
    /** Required by the app monitor's resource policy; sent as `alias` with every batch. */
    alias: string;
    region: string;
    sessionSampleRate: number;
    /** `local` selects the console sink; anything else needs a real app monitor. */
    environment: string;
}

/** Custom event names; other systems build metrics and dashboards on these. */
export const RumEvent = {
    cartItemAdded: 'cart_item_added',
    checkoutStarted: 'checkout_started',
    paymentMethodSelected: 'payment_method_selected',
    paymentSubmitted: 'payment_submitted',
    paymentCompleted: 'payment_completed',
    receiptViewed: 'receipt_viewed',
    searchPerformed: 'search_performed',
    login: 'login',
    accountRegistered: 'account_registered',
    adminConfigSaved: 'admin_config_saved',
    guideStarted: 'guide_started',
    guideStepViewed: 'guide_step_viewed',
    guideCompleted: 'guide_completed',
    guideDismissed: 'guide_dismissed',
    surveyShown: 'survey_shown',
    surveyDismissed: 'survey_dismissed',
    surveySubmitted: 'survey_submitted',
    engagedTime: 'engaged_time'
} as const;

export type RumEventAttributes = Record<string, string | number | boolean>;

/** Local development and tenant-less builds: every event goes to the console. */
export const LOCAL_RUM_CONFIG: TellerOnlineRumConfig = {
    enabled: true,
    appMonitorId: '',
    alias: '',
    region: 'us-west-2',
    sessionSampleRate: 1,
    environment: 'local'
};

/** The one place the DTO is mapped; undefined when the backend sent no RUM section. */
export function readRumConfig(
    dto: RumConfigurationDto | undefined
): TellerOnlineRumConfig | undefined {
    if (!dto) return undefined;

    return {
        enabled: dto.enabled ?? false,
        appMonitorId: dto.appMonitorId ?? '',
        alias: dto.alias ?? '',
        region: dto.region || 'us-west-2',
        sessionSampleRate: dto.sessionSampleRate ?? 1,
        environment: dto.environment ?? ''
    };
}

/** Bucket for search_performed: counts only, never the results themselves. */
export function resultBucket(count: number): '0' | '1' | '2-5' | '6+' {
    if (count <= 0) return '0';
    if (count === 1) return '1';

    return count <= 5 ? '2-5' : '6+';
}
